import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError, confirmDelete } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';
import Switch from 'react-switch';

const BannerPage = () => {
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [formValues, setFormValues] = useState({
    images: null,
    link: '',
    active: false,
    startDate: '',
    endDate: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const limit = parseInt(process.env.REACT_APP_PAGE_LIMIT || 10);  
  // Initialize Bootstrap tooltips when DOM changes (images/table update)
  useEffect(() => {
    if (window.bootstrap && window.bootstrap.Tooltip) {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      tooltipTriggerList.forEach(tooltipTriggerEl => {
        try { new window.bootstrap.Tooltip(tooltipTriggerEl, { html: true, sanitize: false }); } catch (e) { /* ignore */ }
      });
    }
  }, [banners, showModal, showViewModal]);

  const validateImageFile = (file, requiredWidth, requiredHeight) => {
    return new Promise((resolve, reject) => {
      if (!file) return reject('No file provided');
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (!allowedTypes.includes(file.type)) return reject('Only JPG and PNG images are allowed');
      if (file.size > maxSize) return reject('Image size should be less than 2MB');
      const img = new Image();
      img.onload = () => {
        if (requiredWidth && requiredHeight) {
          if (img.width !== requiredWidth || img.height !== requiredHeight) {
            reject(`Image dimensions must be ${requiredWidth}x${requiredHeight}px`);
          } else {
            resolve(true);
          }
        } else resolve(true);
      };
      img.onerror = () => reject('Invalid image file');
      img.src = URL.createObjectURL(file);
    });
  };
  useEffect(() => {
    fetchBanners(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const fetchBanners = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const res = await axios.get(`/admin/api/banners?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}&isadmin=true`);
      console.log('API Response:', res.data); // Debug log
      
      // Handle different response structures
      let bannersData = [];
      let totalCount = 0;
      
      if (res.data.banners) {
        // Standard paginated response
        bannersData = res.data.banners;
        totalCount = res.data.totalCount || res.data.banners.length;
      } else if (res.data.banner) {
        // Single banner response
        bannersData = [res.data.banner];
        totalCount = 1;
      } else if (Array.isArray(res.data)) {
        // Direct array response
        bannersData = res.data;
        totalCount = res.data.length;
      } else {
        // Fallback
        bannersData = [];
        totalCount = 0;
      }
      
      console.log('Processed banners:', bannersData);
      setBanners(bannersData);
      setTotalPages(Math.ceil(totalCount / limit));
    } catch (err) {
      console.error('Failed to fetch Banners:', err);
      console.error('Error details:', err.response?.data);
      toastError('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      images: { required: !editingId }, // Image required only for new banners
      link: { required: true },
      startDate: { required: true },
      endDate: { required: true },
    });

    // Additional validation for file size and type (2MB, JPG/PNG)
    if (formValues.images instanceof File) {
      const maxSize = 2 * 1024 * 1024; // 2MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (formValues.images.size > maxSize) {
        validationErrors.images = 'Image size should be less than 2MB';
      }
      if (!allowedTypes.includes(formValues.images.type)) {
        validationErrors.images = 'Only JPG and PNG images are allowed';
      }
    }

    // Date validation
    if (formValues.startDate && formValues.endDate) {
      const startDate = new Date(formValues.startDate);
      const endDate = new Date(formValues.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Only prevent past start date for new banners. Allow editing existing banners to keep or set past dates.
      if (!editingId && startDate < today) {
        validationErrors.startDate = 'Start date cannot be in the past';
      }
      
      if (endDate <= startDate) {
        validationErrors.endDate = 'End date must be after start date';
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      
      if (formValues.images instanceof File) {
        formData.append('images', formValues.images);
      }
      
      // Match exact API request format
      formData.append('active', formValues.active);
      formData.append('link', formValues.link || '');
      formData.append('startDate', formValues.startDate || '');
      formData.append('endDate', formValues.endDate || '');

      console.log('Sending form data (API format):');
      console.log('- Image file:', formValues.images instanceof File ? formValues.images.name : 'No file');
      console.log('- active:', formValues.active);
      console.log('- link:', formValues.link);
      console.log('- startDate:', formValues.startDate);
      console.log('- endDate:', formValues.endDate);
      
      // Debug: Log all form values before sending
      console.log('Complete form values:', {
        images: formValues.images instanceof File ? formValues.images.name : formValues.images,
        active: formValues.active,
        link: formValues.link,
        startDate: formValues.startDate,
        endDate: formValues.endDate
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      let response;
      if (editingId) {
        response = await axios.put(`/admin/api/banners/${editingId}`, formData, config);
        toastSuccess('Slider updated successfully!');
      } else {
        response = await axios.post('/admin/api/banners', formData, config);
        toastSuccess('Slider added successfully!');
      }
      
      console.log('Save response:', response.data);
      
      // Handle response format consistently with fetchBanners
      if (response.data) {
        if (response.data.Message) {
          console.log('Saved banner data:', response.data.banner);
        } else if (response.data.Message) {
          console.log('Saved banners data:', response.data.banners);
        } else if (response.data.Message) {
          console.log('Save message:', response.data.message);
        }
      }
      
      setCurrentPage(1);
      fetchBanners(1, searchQuery);
      handleCancel();
    } catch (err) {
      console.error('Failed to save Banner:', err);
      toastError(err.response?.data?.message || 'Failed to save Banner');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setFormValues({
      images: null, // Don't preload the image file for editing
      link: banner.link || '',
      active: banner.active || false,
      startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
      endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
    });
    setImagePreview(banner.images || '');
    setEditingId(banner._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmDelete('Are you sure you want to delete this banner?');
    if (confirmed) {
      try {
        await axios.delete(`/admin/api/banners/${id}`);
        toastSuccess('Slider deleted successfully!');
        setCurrentPage(1);
        fetchBanners(1, searchQuery);
      } catch (err) {
        console.error('Failed to delete Banner:', err);
        toastError('Failed to delete Slider');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({ 
      images: null, 
      link: '',
      active: false,
      startDate: '',
      endDate: '',
    });
    setImagePreview('');
    setEditingId(null);
    setErrors({});
  };

  const handleImageChange = (name, file) => {
    setFormValues(prevValues => ({ ...prevValues, [name]: file }));
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview('');
    }
  };

  const handleViewBanner = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const now = new Date().toISOString();
      const payload = {
        active: !currentStatus,
        updatedAt: now,
        activatedAt: !currentStatus ? now : undefined,
        deactivatedAt: currentStatus ? now : undefined,
      };
      const config = { headers: { 'Content-Type': 'application/json' } };
      const response = await axios.put(`/admin/api/banners/${id}`, payload, config);
      console.log('Toggle status response:', response.data);

      setBanners(prev => prev.map(b => {
        if (b._id !== id) return b;
        return {
          ...b,
          active: !currentStatus,
          updatedAt: new Date().toISOString(),
          activatedAt: !currentStatus ? (response.data?.banner?.activatedAt || now) : b.activatedAt,
          deactivatedAt: currentStatus ? (response.data?.banner?.deactivatedAt || now) : b.deactivatedAt,
        };
      }));

      toastSuccess(`Slider ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      console.error('Failed to update slider status:', err);
      console.error('Toggle status error details:', err.response?.data);
      toastError(err.response?.data?.message || 'Failed to update slider status');
    }
  };

  const handleViewToggle = async () => {
    if (!selectedBanner) return;
    try {
      await handleToggleStatus(selectedBanner._id, selectedBanner.active);
      const now = new Date().toISOString();
      setSelectedBanner(prev => prev ? {
        ...prev,
        active: !prev.active,
        updatedAt: now,
        activatedAt: !prev.active ? now : prev.activatedAt,
        deactivatedAt: prev.active ? now : prev.deactivatedAt,
      } : prev);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      // handled in handleToggleStatus
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Slider</h3>
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">slider List</h5>
          <button
            className="btn btn-sm"
            style={{ backgroundColor: '#F94F01', color: '#fff' }}
            onClick={() => {
              setFormValues({
                images: null,
                link: '',
                active: false,
                startDate: '',
                endDate: '',
              });
              setImagePreview('');
              setEditingId(null);
              setErrors({});
              setShowModal(true);
            }}
          >
            + Add New Slider
          </button>
        </div>

        <div className="card-body p-0">
          <table
            className="table table-bordered table-sm mb-0"
            style={{ fontSize: '14px', lineHeight: '2' }}
          >
            <thead className="table-light">
              <tr>
                <th className="py-1 px-2 w-5">Sr no.</th>
                <th className="py-1 px-2">Image</th>
                <th className="py-1 px-2">Link</th>
                <th className="py-1 px-2">Start Date</th>
                <th className="py-1 px-2">End Date</th>
                <th className="py-1 px-2">Status</th>
                <th className="py-1 px-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : banners.length > 0 ? (
                banners.map((banner, idx) => (
                  <tr key={banner._id}>
                    <td className="py-1 px-2">{(currentPage - 1) * limit + idx + 1}</td>
                    <td className="py-1 px-2">
                      {banner.images && banner.images.trim() !== '' ? (
                        <img 
                          src={banner.images} 
                          alt="Banner" 
                          style={{ 
                            width: '60px', 
                            height: '40px', 
                            objectFit: 'cover',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                          onClick={() => window.open(banner.images, '_blank')}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          data-bs-html="true"
                          title={`<div style="text-align:center;"><img src="${banner.images}" style="max-width:220px;max-height:140px;display:block;margin-bottom:6px;border-radius:4px;"/> <div style="font-size:12px;color:#666">${banner.createdAt ? 'Added: ' + new Date(banner.createdAt).toLocaleString() : 'Added: -'}<br/>${banner.updatedAt ? 'Edited: ' + new Date(banner.updatedAt).toLocaleString() : 'Edited: -'}</div></div>`}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) e.target.nextSibling.style.display = 'inline';
                          }}
                        />
                      ) : null}
                      {(!banner.images || banner.images.trim() === '') && (
                        <span className="text-muted">No Image</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {banner.link ? (
                        <a 
                          href={banner.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary text-decoration-none"
                          style={{ fontSize: '12px' }}
                          title={banner.link}
                        >
                          {banner.link.length > 30 ? banner.link.substring(0, 30) + '...' : banner.link}
                        </a>
                      ) : (
                        <span className="text-muted">No Link</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {banner.startDate ? (
                        <span style={{ fontSize: '12px' }}>
                          {new Date(banner.startDate).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      {banner.endDate ? (
                        <span style={{ fontSize: '12px' }}>
                          {new Date(banner.endDate).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      <div title={`Click to ${banner.active ? 'deactivate' : 'activate'}\nCreated: ${banner.createdAt ? new Date(banner.createdAt).toLocaleString() : '-'}\nUpdated: ${banner.updatedAt ? new Date(banner.updatedAt).toLocaleString() : '-'}\nActivated: ${banner.activatedAt ? new Date(banner.activatedAt).toLocaleString() : '-'}\nDeactivated: ${banner.deactivatedAt ? new Date(banner.deactivatedAt).toLocaleString() : '-'}`}>
                        <Switch
                          onChange={() => handleToggleStatus(banner._id, banner.active)}
                          checked={!!banner.active}
                          onColor="#28a745"
                          offColor="#6c757d"
                          uncheckedIcon={false}
                          checkedIcon={false}
                          height={20}
                          width={44}
                          handleDiameter={18}
                        />
                      </div>
                      {banner.activatedAt && (
                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 4 }}>
                          Activated: {new Date(banner.activatedAt).toLocaleString()}
                        </div>
                      )}
                      {banner.deactivatedAt && (
                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 4 }}>
                          Deactivated: {new Date(banner.deactivatedAt).toLocaleString()}
                        </div>
                      )}
                      {banner.updatedAt && (
                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: 4 }}>
                          Updated: {new Date(banner.updatedAt).toLocaleString()}
                        </div>
                      )}
                    </td>
                    <td className="py-1 px-2">
                      <button
                        className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0 text-primary-custom"
                        title="Edit"
                        onClick={() => handleEdit(banner)}
                      >
                        <FaEdit size={14} />
                      </button>
                      {/* <button
                                              className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0 text-danger"
                                              title="Delete"
                                              onClick={() => handleDelete(banner._id)}
                                            >
                                              <FaTrash size={14} />
                                            </button> */}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-2">No Banners found.</td>
                </tr>
              )}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end align-items-center gap-2 p-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              >
                &laquo;
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  className={`btn btn-sm page-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Custom Banner Form Modal */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Slider' : 'Add Slider'}</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCancel}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  {/* Image Upload */}
                  <div className="mb-3">
                    <label className="form-label">Slider Image <span className="text-danger">*</span>
                     <button
                        type="button"
                        className="btn btn-secondary btn-sm rounded-pill px-2 py-0"
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        data-bs-html="true"
                        title={`You may upload one image. Allowed: jpg, jpeg, png. Max 2MB. Required size: 400x180 px`}
                      >
                        ?
                      </button>
                      </label>
                    <div className="d-flex align-items-center gap-2">
                      <input
                        type="file"
                        className={`form-control ${errors.images ? 'is-invalid' : ''}`}
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          try {
                            // Banner required dimensions: 400x180
                            await validateImageFile(file, 400, 180);
                            setFormValues(prev => ({ ...prev, images: file }));
                            setImagePreview(URL.createObjectURL(file));
                            if (errors.images) setErrors(prev => ({ ...prev, images: '' }));
                          } catch (err) {
                            setErrors(prev => ({ ...prev, images: err }));
                            setImagePreview('');
                            setFormValues(prev => ({ ...prev, images: null }));
                            toastError(err);
                          }
                        }}
                      />
                    </div>
                    {errors.images && <div className="invalid-feedback">{errors.images}</div>}
                    {imagePreview && (
                      <div className="mt-2">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                          className="img-thumbnail"
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          title={editingId ? `Edited: ${new Date().toLocaleString()}` : `Added: ${new Date().toLocaleString()}`}
                        />
                      </div>
                    )}
                  </div>

                  {/* Banner Link */}
                  <div className="mb-3">
                    <label className="form-label">Slider Link <span className="text-danger">*</span></label>
                    <input
                      type="url"
                      className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                      placeholder="Enter Slider URL (e.g., https://example.com)"
                      value={formValues.link}
                      onChange={(e) => {
                        console.log('Link changed:', e.target.value);
                        setFormValues(prev => ({ ...prev, link: e.target.value }));
                        if (errors.link) {
                          setErrors(prev => ({ ...prev, link: '' }));
                        }
                      }}
                    />
                    {errors.link && <div className="invalid-feedback">{errors.link}</div>}
                  </div>

                  <div className="row">
                    {/* Start Date */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Start Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                        value={formValues.startDate}
                        onChange={(e) => {
                          console.log('Start date changed:', e.target.value);
                          setFormValues(prev => ({ ...prev, startDate: e.target.value }));
                          if (errors.startDate) {
                            setErrors(prev => ({ ...prev, startDate: '' }));
                          }
                        }}
                      />
                      {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                    </div>

                    {/* End Date */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">End Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                        value={formValues.endDate}
                        onChange={(e) => {
                          console.log('End date changed:', e.target.value);
                          setFormValues(prev => ({ ...prev, endDate: e.target.value }));
                          if (errors.endDate) {
                            setErrors(prev => ({ ...prev, endDate: '' }));
                          }
                        }}
                      />
                      {errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
                    </div>
                  </div>

                  {/* Active Status */}
                    <div className="mb-3">
                      <label className="form-label d-block">Active Status</label>
                      <div>
                        <Switch
                          onChange={(checked) => setFormValues(prev => ({ ...prev, active: checked }))}
                          checked={!!formValues.active}
                          onColor="#28a745"
                          offColor="#6c757d"
                          uncheckedIcon={false}
                          checkedIcon={false}
                          height={20}
                          width={44}
                          handleDiameter={18}
                        />
                      </div>
                    </div>
                </form>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#F94F01', color: 'white' }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Banner Modal */}
      {showViewModal && selectedBanner && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Banner Preview</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowViewModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <div className="mb-3">
                  {selectedBanner.images && selectedBanner.images.trim() !== '' ? (
                    <img
                      src={selectedBanner.images}
                      alt="Banner"
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '500px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : (
                    <div 
                      className="d-flex align-items-center justify-content-center bg-light"
                      style={{ 
                        width: '100%', 
                        height: '300px',
                        border: '2px dashed #ccc',
                        borderRadius: '8px'
                      }}
                    >
                      <span className="text-muted">No Image Available</span>
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Status:</strong> 
                      <span className={`ms-2 badge ${selectedBanner.active ? 'badge-success' : 'badge-secondary'}`}>
                        {selectedBanner.active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                    <div style={{ marginTop: 6 }}>
                      <button
                        type="button"
                        className={`btn btn-sm ${selectedBanner.active ? 'btn-outline-danger' : 'btn-success'}`}
                        onClick={handleViewToggle}
                      >
                        {selectedBanner.active ? 'Deactivate' : 'Activate'}
                      </button>
                    </div>
                  </div>
                    <div className="col-md-6">
                      <p><strong>Created:</strong> {selectedBanner.createdAt ? new Date(selectedBanner.createdAt).toLocaleString() : '-'}</p>
                      <p style={{ marginTop: 6 }}><strong>Activated:</strong> {selectedBanner.activatedAt ? new Date(selectedBanner.activatedAt).toLocaleString() : '-'}</p>
                      <p style={{ marginTop: 6 }}><strong>Deactivated:</strong> {selectedBanner.deactivatedAt ? new Date(selectedBanner.deactivatedAt).toLocaleString() : '-'}</p>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Updated:</strong> {selectedBanner.updatedAt ? new Date(selectedBanner.updatedAt).toLocaleString() : '-'}</p>
                    </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <p><strong>Link:</strong> 
                      {selectedBanner.link ? (
                        <a href={selectedBanner.link} target="_blank" rel="noopener noreferrer" className="ms-2 text-primary">
                          {selectedBanner.link}
                        </a>
                      ) : (
                        <span className="ms-2 text-muted">No link provided</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Start Date:</strong> 
                      <span className="ms-2">
                        {selectedBanner.startDate ? new Date(selectedBanner.startDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>End Date:</strong> 
                      <span className="ms-2">
                        {selectedBanner.endDate ? new Date(selectedBanner.endDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                {selectedBanner.link && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => window.open(selectedBanner.link, '_blank')}
                  >
                    Visit Link
                  </button>
                )}
                {selectedBanner.images && selectedBanner.images.trim() !== '' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => window.open(selectedBanner.images, '_blank')}
                  >
                    Open Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerPage;
